import { motion } from 'framer-motion';
import { Download, FileText, ArrowRight, Building2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useMemo, useState, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { SEO } from '../components/SEO';

const fadeUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.6, ease: 'easeOut' }
};

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const downloads = [
  {
    id: 'produktkatalog',
    title: { de: 'Produktkatalog', en: 'Product Catalog' },
    description: {
      de: 'Unser vollständiger Produktkatalog mit allen E-Learning-Modulen, Kursbeschreibungen, verfügbaren Sprachen und Rechtskontexten.',
      en: 'Our complete product catalog with all e-learning modules, course descriptions, available languages and legal contexts.'
    },
    url: {
      de: `${BACKEND_URL}/api/static/produktkatalog_de.pdf`,
      en: `${BACKEND_URL}/api/static/produktkatalog_en.pdf`,
    },
    format: 'PDF',
    date: '06.03.2026',
    category: 'produkte',
  },
  {
    id: 'besucherunterweisung',
    title: { de: 'Besucherunterweisungssystem', en: 'Visitor Instruction System' },
    description: {
      de: 'Produktinformation zum Besucherunterweisungssystem – flexible und effiziente Unterweisung von Besuchern, Fremdfirmen und externen Personen.',
      en: 'Product information about the Visitor Instruction System – flexible and efficient instruction of visitors, external companies and external persons.'
    },
    url: {
      de: '/assets/visitor/besucherunterweisungssystem_de.pdf',
      en: '/assets/visitor/visitor_instruction_en.pdf',
    },
    format: 'PDF',
    date: '09.03.2026',
    category: 'produkte',
  },
  {
    id: 'gewerbe-datenverarbeitung',
    title: { de: 'Datenverarbeitung und Informationstechnik', en: 'Data Processing and Information Technology' },
    description: {
      de: 'Gewerbeberechtigung: Dienstleistungen in der automatischen Datenverarbeitung und Informationstechnik (GISA: 27845769)',
      en: 'Trade license: Services in automatic data processing and information technology (GISA: 27845769)'
    },
    url: '/assets/firmendokumente/datenverarbeitung_und_informationstechnik.pdf',
    format: 'PDF',
    date: '10.03.2026',
    category: 'firma',
  },
  {
    id: 'gewerbe-handelsgewerbe',
    title: { de: 'Handelsgewerbe', en: 'Trade Business' },
    description: {
      de: 'Gewerbeberechtigung: Handelsgewerbe mit Ausnahme der reglementierten Handelsgewerbe und Handelsagent (GISA: 27845868)',
      en: 'Trade license: Trade business with the exception of regulated trade businesses and commercial agents (GISA: 27845868)'
    },
    url: '/assets/firmendokumente/handelsgewerbe.pdf',
    format: 'PDF',
    date: '10.03.2026',
    category: 'firma',
  },
  {
    id: 'gewerbe-unternehmensberatung',
    title: { de: 'Unternehmensberatung', en: 'Management Consulting' },
    description: {
      de: 'Gewerbeberechtigung: Unternehmensberatung einschließlich der Unternehmensorganisation (GISA: 28820222)',
      en: 'Trade license: Management consulting including business organisation (GISA: 28820222)'
    },
    url: '/assets/firmendokumente/unternehmensberatung.pdf',
    format: 'PDF',
    date: '10.03.2026',
    category: 'firma',
  },
  {
    id: 'gewerbe-verlag',
    title: { de: 'Buch-, Kunst- und Musikalienverlag', en: 'Book, Art and Music Publishing' },
    description: {
      de: 'Gewerbeberechtigung: Buch-, Kunst- und Musikalienverlag (GISA: 29576166)',
      en: 'Trade license: Book, art and music publishing (GISA: 29576166)'
    },
    url: '/assets/firmendokumente/buch_kunst_und_musikalienverlag.pdf',
    format: 'PDF',
    date: '10.03.2026',
    category: 'firma',
  },
  {
    id: 'gewerbe-werbeagentur',
    title: { de: 'Werbeagentur', en: 'Advertising Agency' },
    description: {
      de: 'Gewerbeberechtigung: Werbeagentur (GISA: 32937213)',
      en: 'Trade license: Advertising agency (GISA: 32937213)'
    },
    url: '/assets/firmendokumente/werbeagentur.pdf',
    format: 'PDF',
    date: '10.03.2026',
    category: 'firma',
  },
  {
    id: 'uid-bestaetigung',
    title: { de: 'UID-Bestätigung', en: 'VAT ID Confirmation' },
    description: {
      de: 'Bestätigung der Umsatzsteuer-Identifikationsnummer (UID) der e-Matrix Innovations GmbH.',
      en: 'VAT identification number (UID) confirmation for e-Matrix Innovations GmbH.'
    },
    url: '/assets/firmendokumente/uid_bestaetigung.pdf',
    format: 'PDF',
    date: '10.03.2026',
    category: 'firma',
  },
];

function DownloadCard({ item, index, language }) {
  const downloadUrl = typeof item.url === 'object' ? (item.url[language] || item.url.de) : item.url;
  const displayLang = typeof item.url === 'object' ? language.toUpperCase() : item.lang;

  return (
    <motion.div
      className="bg-white rounded-2xl p-6 md:p-8 border border-[#C8D8E8] hover:shadow-lg transition-shadow"
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: index * 0.1 }}
      data-testid={`download-${item.id}`}
    >
      <div className="flex items-start gap-6">
        <div className="w-14 h-14 bg-red-50 rounded-xl flex items-center justify-center flex-shrink-0">
          <FileText className="w-7 h-7 text-red-500" />
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-2 flex-wrap">
            <h3 className="font-semibold text-xl text-[#0D2440]">
              {item.title[language]}
            </h3>
            <span className="px-2 py-0.5 bg-[#E7F0FA] text-[#5A7A9B] text-xs font-medium rounded border border-[#C8D8E8]">
              {item.format}
            </span>
            {displayLang && (
              <span className="px-2 py-0.5 bg-[#2E5E99]/10 text-[#2E5E99] text-xs font-medium rounded border border-[#2E5E99]/20">
                {displayLang}
              </span>
            )}
          </div>
          <p className="text-[#5A7A9B] mb-4">
            {item.description[language]}
          </p>
          <div className="flex items-center gap-4">
            <a
              href={downloadUrl}
              download
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#0D2440] hover:bg-[#162D4A] text-white rounded-xl font-medium text-sm transition-colors"
              data-testid={`download-btn-${item.id}`}
            >
              <Download className="w-4 h-4" />
              {language === 'de' ? 'Herunterladen' : 'Download'}
            </a>
            <span className="text-xs text-[#94A3B8]">
              {language === 'de' ? 'Stand:' : 'Date:'} {item.date}
            </span>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

export default function DownloadsPage() {
  const { language } = useLanguage();
  const [catalogDate, setCatalogDate] = useState(null);

  useEffect(() => {
    fetch(`${BACKEND_URL}/api/catalog-info`)
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => data && setCatalogDate(data))
      .catch(() => {});
  }, []);

  // Memoize the two filtered lists so they aren't recomputed on every render.
  const productDownloads = useMemo(
    () =>
      downloads
        .filter((d) => d.category === 'produkte')
        .map((d) =>
          d.id === 'produktkatalog' && catalogDate
            ? { ...d, date: catalogDate[language] || catalogDate.de || d.date }
            : d
        ),
    [catalogDate, language]
  );
  const companyDownloads = useMemo(
    () => downloads.filter((d) => d.category === 'firma'),
    []
  );

  return (
    <div className="min-h-screen pt-20 bg-[#E7F0FA]" data-testid="downloads-page">
      <SEO
        title={language === 'de' ? 'Downloads - Produktkataloge & Broschüren' : 'Downloads - Product Catalogs & Brochures'}
        description={language === 'de' 
          ? 'Produktkataloge, Broschüren und Datenblätter zum Herunterladen. E-Learning Module für Arbeitssicherheit, Cyber Security, DSGVO. PDF Downloads.' 
          : 'Product catalogs, brochures and datasheets available for download. E-learning modules for workplace safety, cyber security, GDPR. PDF downloads.'}
        keywords={language === 'de'
          ? 'E-Learning Produktkatalog Download, Arbeitssicherheit Broschüre, Unterweisungssystem Datenblatt, e-Matrix PDF, Cyber Security Training Download'
          : 'E-Learning Product Catalog Download, Workplace Safety Brochure, Training System Datasheet, e-Matrix PDF, Cyber Security Training Download'}
        path="/downloads"
        lang={language}
      />
      <section className="py-16 md:py-20 bg-gradient-to-br from-[#E7F0FA] via-white to-[#dde9f5]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div {...fadeUp}>
            <Download className="w-12 h-12 text-[#2E5E99] mx-auto mb-6" />
            <h1 className="text-4xl md:text-5xl font-bold text-[#0D2440] mb-6">
              Downloads
            </h1>
            <p className="text-lg text-[#5A7A9B] max-w-2xl mx-auto">
              {language === 'de'
                ? 'Broschüren, Datenblätter und weitere Materialien zum Herunterladen.'
                : 'Brochures, data sheets and other materials available for download.'}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Downloads List */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Product Downloads */}
          <div className="space-y-6">
            {productDownloads.map((item, index) => (
              <DownloadCard key={item.id} item={item} index={index} language={language} />
            ))}
          </div>

          {/* Firmendokumente Section */}
          <div className="mt-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="flex items-center gap-3 mb-8"
            >
              <Building2 className="w-7 h-7 text-[#2E5E99]" />
              <h2 className="text-2xl font-bold text-[#0D2440]" data-testid="firmendokumente-heading">
                {language === 'de' ? 'Firmendokumente' : 'Company Documents'}
              </h2>
            </motion.div>
            <div className="space-y-6">
              {companyDownloads.map((item, index) => (
                <DownloadCard key={item.id} item={item} index={index} language={language} />
              ))}
            </div>
          </div>

          {/* CTA */}
          <motion.div
            {...fadeUp}
            className="mt-16 bg-[#0D2440] rounded-2xl p-8 md:p-12 text-center"
          >
            <h2 className="text-2xl font-bold text-white mb-4">
              {language === 'de' ? 'Individuelle Unterlagen benötigt?' : 'Need custom documents?'}
            </h2>
            <p className="text-white/70 mb-6">
              {language === 'de'
                ? 'Kontaktieren Sie uns gerne, wenn Sie bestimmte Unterlagen oder Informationen benötigen.'
                : 'Feel free to contact us if you need specific documents or information.'}
            </p>
            <Link to="/contact" data-testid="downloads-contact-cta">
              <Button className="bg-[#2E5E99] hover:bg-[#1E4F85] text-white h-12 px-8">
                {language === 'de' ? 'Kontakt aufnehmen' : 'Get in Touch'}
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
